import { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { updateDownloadProgress } from '../components/common/DownloadProgress';

/**
 * Custom hook for downloading files
 * 
 * SUPPORTED FILE TYPES FOR DOWNLOAD:
 * - Documents: PDF, Word (.doc, .docx), PowerPoint (.ppt, .pptx), Excel (.xls, .xlsx), Text, CSV
 * - Images: JPEG, PNG, GIF  
 * - Compressed: ZIP, RAR, 7Z, TAR, GZIP, BZIP2, XZ
 * - All file types uploaded to the system are downloadable
 * 
 * DOWNLOAD BEHAVIOR:
 * - Files are downloaded with 'Content-Disposition: attachment' header
 * - File metadata is included in 'X-File-Info' response header
 * - Progress tracking is available for large files
 * - Original filename is preserved when possible
 * 
 * @param {string} persona - The user persona ('customer' or 'admin'/'super_admin')  
 * @returns {object} - { downloadFile, downloading, error, getFileInfo }
 */
export const useFileDownload = (persona = 'customer') => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Extract and parse file information from response headers
   * @param {object} headers - Response headers object
   * @returns {object|null} - Parsed file info or null if not available
   */
  const parseFileInfo = (headers) => {
    const fileInfoHeader = headers['x-file-info'];
    if (fileInfoHeader) {
      try {
        return JSON.parse(fileInfoHeader);
      } catch (e) {
        console.warn('Could not parse file info from headers:', e);
        return null;
      }
    }
    return null;
  };

  /**
   * Get file information without downloading (uses dedicated file info endpoint)
   * @param {number|string} fileId - The file ID
   * @returns {Promise<object|null>} - File info object or null if not found
   */
  const getFileInfo = async (fileId) => {
    if (!fileId) {
      return null;
    }

    try {
      const endpoint = persona === 'customer' 
        ? `/api/files/${fileId}`
        : `/api/files/admin/${fileId}`;

      const response = await axiosInstance.get(endpoint);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return null;
    } catch (err) {
      console.error('Error getting file info:', err);
      return null;
    }
  };

  const downloadFile = async (fileId, fileName = null) => {
    if (!fileId) {
      console.error('File ID is required for download');
      return;
    }

    // Use fileId as the unique identifier for progress tracking
    const progressId = `download_${fileId}`;

    try {
      setDownloading(true);
      setError(null);
      
      // Start progress tracking
      updateDownloadProgress(progressId, { 
        downloading: true, 
        progress: 0, 
        fileName: fileName 
      });

      // Determine endpoint based on persona
      const endpoint = persona === 'customer' 
        ? `/api/files/download/${fileId}`
        : `/api/files/admin/download/${fileId}`;

      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          updateDownloadProgress(progressId, { 
            downloading: true, 
            progress: percentCompleted,
            fileName: fileName 
          });
        },
      });

      // Get file info from headers if available
      let finalFileName = fileName;
      const parsedFileInfo = parseFileInfo(response.headers);
      if (parsedFileInfo && !finalFileName) {
        finalFileName = parsedFileInfo.original_name || parsedFileInfo.name;
      }

      // Fallback filename
      if (!finalFileName) {
        finalFileName = `download_${fileId}`;
      }

      // Create download link
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Clear progress on successful download
      updateDownloadProgress(progressId, { 
        downloading: false, 
        progress: 100 
      });

      return true;
    } catch (err) {
      console.error('Error downloading file:', err);
      const errorMessage = err.response?.data?.message || 'Failed to download file';
      setError(errorMessage);
      
      // Clear progress on error
      updateDownloadProgress(progressId, { 
        downloading: false, 
        progress: 0 
      });
      
      return false;
    } finally {
      setDownloading(false);
    }
  };

  return {
    downloadFile,
    downloading,
    error,
    getFileInfo,
  };
};

export default useFileDownload;
